import { Select } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch } from '@/store/hooks.ts';
import { selectOptionCurrency, selectOptionLegsProperties } from '@/store/option/selectors/optionSelectors.ts';
import type { LegId, OptionProductName } from '@/store/option/state.ts';
import { resetOptionThunk } from '@/store/option/thunks/thunk.ts';

type TypeProps = {
  tileId: string;
};

export function Type({ tileId }: TypeProps) {
  const [checkOption] = useCheckOption();
  const dispatch = useAppDispatch();
  const {
    currentUser: {
      featureToggles: { optionBinary, optionTrigger },
    },
  } = useUserContext();

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const { formatMessage } = useIntl();

  const legId: LegId = `${tileId}/0`;
  const { currencyPair, currency1, currency2, optionType, productName, uiState } = useOptionSelector((option) => {
    const legProperties = selectOptionLegsProperties(option, legId);
    const { currencyPair, currency1, currency2 } = selectOptionCurrency(option);

    return {
      optionType: legProperties?.type,
      currencyPair,
      currency1,
      currency2,
      productName: option.type,
      uiState: option.properties.uiState,
    };
  });

  const handleOptionTypeChange = (value: OptionProductName) => {
    sgwtWebAnalytics?.trackEvent('Option', 'Change option type', value);
    dispatch(resetOptionThunk({ tileId, currencyPair, newType: value, uiState }));
  };

  const optionTypes = (
    ['Vanilla', 'Trigger', 'Binary', 'Straddle', 'Strangle', 'RiskReversal'] as OptionProductName[]
  ).filter((type) => (type !== 'Trigger' || optionTrigger) && (type !== 'Binary' || optionBinary));

  return (
    <OptionFormGroup label={<FormattedMessage id="option.fields.type" values={{ inStrategyPanel: false }} />}>
      <Select<OptionProductName>
        data-e2e="option-type-select"
        className="form-select-sm"
        itemToString={(type) => formatMessage({ id: `option.type.${type}` }, { currency1, currency2 })}
        selectedItemValue={productName}
        onChange={handleOptionTypeChange}
        itemsAsObjects={optionTypes}
      />
    </OptionFormGroup>
  );
}
